import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { writingAPI } from '@/lib/api';
import { PenLine, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const CHECK_LABELS = {
  looks_typed: { text: 'Typed by the child', tone: 'bg-emerald-100' },
  mostly_dictated: { text: 'Mostly spoken (recordings kept)', tone: 'bg-sky-100' },
  mostly_pasted: { text: 'Mostly pasted', tone: 'bg-rose-100' },
  worth_a_look: { text: 'Worth a look', tone: 'bg-amber-100' },
  incomplete_record: { text: 'Typing record incomplete', tone: 'bg-gray-100' },
};

const VERDICTS = {
  consistent: 'Could explain their writing',
  unsure: 'Unsure: worth a conversation',
  mismatch: 'Could not explain their writing',
  unchecked: 'Not checked automatically',
};

const pct = (x) => `${Math.round((x || 0) * 100)}%`;

const rhythmText = (c) => {
  if (!c) return null;
  if (c.status === 'matches') return 'Typing rhythm matches this child\'s usual.';
  if (c.status === 'different') return 'Typing rhythm is different from this child\'s usual.';
  if (c.status === 'learning') return `Still learning this child's usual typing (${c.pieces} of 4 pieces so far).`;
  return 'Not enough typing in this piece to compare.';
};

const SettingsBox = ({ studentId, settings }) => {
  const qc = useQueryClient();
  const [monitor, setMonitor] = useState(false);
  const [dictation, setDictation] = useState(false);
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setMonitor(!!settings.process_monitoring);
      setDictation(!!settings.dictation);
      setConsent(!!settings.consent_version);
    }
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      await writingAPI.saveSettings(studentId, { process_monitoring: monitor, dictation, consent });
      await qc.invalidateQueries({ queryKey: ['guardian-writing', studentId] });
      toast.success('Writing settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const anyOn = monitor || dictation;
  return (
    <div className="border-2 border-black p-4 mb-4 bg-gray-50" data-testid="writing-settings">
      <div className="flex items-center gap-2 mb-2"><ShieldCheck size={18} /><h4 className="font-black uppercase text-sm">Check that the writing is theirs</h4></div>
      <label className="flex items-start gap-2 text-sm mb-2">
        <input type="checkbox" checked={monitor} onChange={(e) => setMonitor(e.target.checked)} className="mt-1" data-testid="setting-monitor" />
        <span><b>Record typing timing.</b> We note how fast your child types, how often they pause, and whether text was pasted. We store timing only, never which keys were pressed. After a few pieces we can tell you if the typing rhythm looks unlike your child's usual.</span>
      </label>
      <label className="flex items-start gap-2 text-sm mb-2">
        <input type="checkbox" checked={dictation} onChange={(e) => setDictation(e.target.checked)} className="mt-1" data-testid="setting-dictation" />
        <span><b>Allow the microphone.</b> Your child can speak instead of type, and for essays they answer two spoken questions about their own writing. Recordings are kept so you can listen.</span>
      </label>
      {anyOn && (
        <label className="flex items-start gap-2 text-sm mb-3">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" data-testid="setting-consent" />
          <span>I am this child's parent or guardian and I agree to the above. These are signals for me, not proof, and nothing is shown to my child as a verdict.</span>
        </label>
      )}
      <button onClick={save} disabled={saving || (anyOn && !consent)}
        className="px-4 py-2 border-2 border-black bg-white font-bold text-sm disabled:opacity-40" data-testid="setting-save">
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
};

const Audio = ({ studentId, pieceId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { setData((await writingAPI.audio(studentId, pieceId)).data); } catch { toast.error('Could not load the recordings'); }
    setLoading(false);
  };
  if (!data) {
    return <button onClick={load} disabled={loading} className="text-sm underline font-bold" data-testid="load-audio">{loading ? 'Loading…' : 'Listen to the recordings'}</button>;
  }
  return (
    <div className="space-y-2">
      {data.dictations.map((d, i) => (
        <div key={d.id}><p className="text-xs text-gray-500">Spoken part {i + 1}</p><audio controls src={d.url} className="w-full" /></div>
      ))}
      {data.followup.map((f) => (
        <div key={f.q_index}><p className="text-xs text-gray-500">Answer to question {f.q_index + 1}</p><audio controls src={f.url} className="w-full" /></div>
      ))}
    </div>
  );
};

const Piece = ({ studentId, p }) => {
  const a = p.authorship;
  const f = a?.features;
  const check = a?.check ? CHECK_LABELS[a.check] : null;
  return (
    <details className="border-2 border-black p-3">
      <summary className="cursor-pointer text-sm font-bold">
        {new Date(p.created_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        {' · '}Step {p.level} · {p.words} words
        {p.status === 'graded' ? ` · score ${p.score}` : ' · not graded'}
        {p.moved === 'up' ? ' · moved up' : ''}{p.moved === 'down' ? ' · stepped back' : ''}
        {p.concerning ? ' · please read' : ''}
        {check && <span className={`ml-2 px-2 py-0.5 text-xs border border-black ${check.tone}`}>{check.text}</span>}
      </summary>
      <p className="text-xs text-gray-500 mt-2">{p.prompt}</p>
      <p className="text-sm mt-2 whitespace-pre-wrap">{p.text}</p>
      {p.feedback && <p className="text-sm mt-2 bg-amber-50 p-2"><b>Feedback:</b> {p.feedback}</p>}

      {f && (
        <div className="mt-3 border-t-2 border-black pt-2 text-sm" data-testid="piece-authorship">
          <p className="font-bold">How it was written</p>
          <p>Typed {pct(f.typed_share)} · pasted {pct(f.pasted_share)} · spoken {pct(f.dictated_share)}
            {f.flow_wpm ? ` · about ${Math.round(f.flow_wpm)} words a minute` : ''}
            {f.pauses != null ? ` · ${f.pauses} pauses` : ''}{f.correction_rate != null ? ` · fixed ${pct(f.correction_rate)} of what they typed` : ''}
          </p>
          {rhythmText(a.comparison) && <p className="text-gray-600">{rhythmText(a.comparison)}</p>}
          {!f.record_complete && <p className="text-gray-600">Some text appeared that the typing record could not explain. Some keyboards do this.</p>}
          {a.signals?.map((s, i) => (
            <p key={i} className={s.severity === 'high' ? 'text-rose-700' : s.severity === 'medium' ? 'text-amber-700' : 'text-gray-600'}>• {s.note}</p>
          ))}
        </div>
      )}

      {p.followup && (
        <div className="mt-3 border-t-2 border-black pt-2 text-sm" data-testid="piece-followup">
          <p className="font-bold">Spoken follow-up{p.followup.verdict ? `: ${VERDICTS[p.followup.verdict] || p.followup.verdict}` : ' (not finished)'}</p>
          {p.followup.note && <p className="text-gray-600">{p.followup.note}</p>}
          {p.followup.questions.map((q, i) => {
            const ans = p.followup.answers.find((x) => x.q_index === i);
            return (
              <div key={i} className="mt-1">
                <p className="text-gray-600">Q: {q}</p>
                <p>{ans ? `"${ans.transcript}"` : 'Not answered'}</p>
              </div>
            );
          })}
        </div>
      )}
      {p.has_audio && <div className="mt-3"><Audio studentId={studentId} pieceId={p.id} /></div>}
    </details>
  );
};

// A parent's view of the child's writing: step, what they wrote, and how it was written.
const GuardianWritingPanel = ({ studentId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['guardian-writing', studentId],
    queryFn: async () => (await writingAPI.forStudent(studentId, 20)).data,
    enabled: !!studentId,
  });
  const progress = data?.progress;
  const pieces = data?.pieces || [];

  return (
    <div className="border-4 border-black bg-white p-5 brutal-shadow-sm" data-testid="guardian-writing-panel">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <PenLine size={20} />
        <h3 className="text-xl font-black uppercase">Writing</h3>
        {progress && (
          <span className="text-sm font-bold text-gray-600" data-testid="guardian-writing-level">
            Step {progress.level} of {progress.max_level} · {progress.level_name} · {progress.total_pieces} pieces
          </span>
        )}
      </div>
      {data && <SettingsBox studentId={studentId} settings={data.settings} />}
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : pieces.length === 0 ? (
        <p className="text-sm text-gray-500">No writing yet. The Writing Studio is on your child's home screen.</p>
      ) : (
        <div className="space-y-2">
          {pieces.map((p) => <Piece key={p.id} studentId={studentId} p={p} />)}
        </div>
      )}
    </div>
  );
};

export default GuardianWritingPanel;
